import { FC } from 'react';
import {
  Badge,
  Box,
  Center,
  GridItem,
  SimpleGrid,
  Skeleton,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';

import {
  useGetFileVersionHistoryQuery,
  VersionHistoryFileFieldsFragment,
} from '../../graphql/generated';
import { useRestFileDownload } from '../../hooks/use-rest-file-download';
import { DebounceLoadingState } from '../../ui/debounce-loading/debounce-loading';
import { ErrorState } from '../../ui/errors/error-state';
import { formatFileSize } from '../../utils/file/file';
import { FileStatus } from '../file-browser/file-status/file-status';
import { FileDownloadIconButton } from '../file-download-button/file-download-button';
import { CreatedAt } from './created-at';
import {
  formatVersionGroupDate,
  groupVersionsByCreatedAt,
  sortVersionsByLatest,
} from './utils';

type Props = {
  cedantId: string;
  fileId: string;
};

export const FileVersionHistory: FC<Props> = ({ cedantId, fileId }) => {
  const { data, error } = useGetFileVersionHistoryQuery({
    variables: {
      cedant: cedantId,
      fileId,
    },
  });

  const { downloadFile } = useRestFileDownload();

  if (error) {
    return (
      <ErrorState
        h="80vh"
        title="Unable to load the version history"
        subtitle="Please reach out to the technical support team for further assistance."
        data-testid="error"
      />
    );
  }

  if (!data) {
    return (
      <DebounceLoadingState>
        <VStack spacing={5}>
          {Array.from({ length: 5 }, (_, i) => (
            <Skeleton key={`skeleton-loading-${i}`} h={20} w="100%" />
          ))}
        </VStack>
      </DebounceLoadingState>
    );
  }

  if (!data.cedant?.file) {
    return (
      <Center flex={1}>
        <ErrorState
          title="File Not Found"
          subtitle="File version history could not be retrieved for this file. It may have been moved or deleted."
        />
      </Center>
    );
  }

  const handleDownload = (
    file: VersionHistoryFileFieldsFragment,
    versionId: string
  ) => async () => {
    await downloadFile({
      cedantName: file.cedant,
      id: fileId,
      actualFileName: file.name,
      version: versionId,
    });
  };

  const versions = groupVersionsByCreatedAt(
    sortVersionsByLatest(data.cedant.file.versions)
  );

  return (
    <Stack>
      {Object.entries(versions).map(([date, versions], groupIndex) => (
        <Stack
          key={date}
          _notFirst={{ marginTop: 12 }}
          data-testid="version-date-group"
        >
          <Box>
            <Text
              fontSize="sm"
              color="gray.500"
              fontWeight="600"
              casing="uppercase"
              data-testid="version-date-group-title"
            >
              {formatVersionGroupDate(date)}
            </Text>
          </Box>
          <Box borderY="1px" borderColor="gray.100">
            {versions.map((version, versionIndex) => {
              const isCurrentVersion = groupIndex === 0 && versionIndex === 0;
              return (
                <Box
                  key={version.id}
                  paddingY={5}
                  borderBottom="1px"
                  borderColor="gray.100"
                  _last={{ borderBottom: 0 }}
                  data-testid="version-list-item"
                >
                  <SimpleGrid columns={6} spacing={4}>
                    <GridItem colSpan={2}>
                      <Box>
                        {isCurrentVersion && (
                          <Badge fontSize="xs">Current Version</Badge>
                        )}
                      </Box>
                      <Text
                        isTruncated
                        fontSize="sm"
                        paddingLeft="1"
                        fontWeight="500"
                        color="gray.800"
                        data-testid="file-name"
                      >
                        {version.name}
                      </Text>
                      <Text
                        fontSize="sm"
                        paddingLeft="1"
                        color="gray.500"
                        data-testid="file-created-at"
                      >
                        <CreatedAt object={version} />
                      </Text>
                    </GridItem>
                    <GridItem
                      d="flex"
                      alignItems="center"
                      justifyContent="center"
                      data-testid="file-status"
                    >
                      <FileStatus object={version} />
                    </GridItem>
                    <GridItem
                      d="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Box>
                        <Text
                          width="36"
                          isTruncated
                          fontSize="sm"
                          align="center"
                          color="gray.600"
                          data-testid="file-uploaded-by"
                          title={version.updatedBy?.name ?? ''}
                        >
                          {version.updatedBy?.name}
                        </Text>
                      </Box>
                    </GridItem>
                    <GridItem
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <Text
                        fontSize="sm"
                        color="gray.600"
                        data-testid="file-size"
                      >
                        {formatFileSize(version.size)}
                      </Text>
                    </GridItem>
                    <GridItem
                      d="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <FileDownloadIconButton
                        file={version}
                        onDownload={handleDownload(
                          version,
                          versions[versionIndex].id
                        )}
                      />
                    </GridItem>
                  </SimpleGrid>
                </Box>
              );
            })}
          </Box>
        </Stack>
      ))}
    </Stack>
  );
};
